/**
 * Las tres fotos del reporte: frente, espalda y perfil, relajado.
 *
 * Se subían en los check-ins, que es donde se VEN, no donde se piden. Ahora hay un solo
 * sitio para subirlas -- el reporte -- y la rejilla de check-ins se queda para mirarlas.
 *
 * Debajo de cada hueco va la del mes pasado en pequeño. No es decoración: es para que se
 * coloque igual (misma distancia, misma luz, mismo sitio), que es lo único que hace que
 * dos fotos de meses distintos se puedan comparar.
 *
 * Y las indicaciones escritas, siempre a la vista. OJO: los textos de abajo son un
 * resumen mío. Los buenos están en el Typeform y en el Google Form del reporte, en la
 * descripción de cada campo, y NO están en los .docx exportados: hay que sacarlos del
 * formulario en vivo. Cuando lleguen, se cambian aquí y ya.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Check } from 'lucide-react';
import { mensajeDeError } from '../../lib/mensajeDeError';
import { diaLocal, hoyLocal } from '../../lib/horaEspana';
import VisorDeFoto from '../ui/VisorDeFoto';

// EL ORDEN ES EL SUYO Y LOS ROTULOS LLEVAN EL «DE» (Jesús, 9-09, respuesta 8).
//
// Su documento pedía «Frente · Perfil · Espalda» y se aplicó tal cual; al verlo dijo que
// no: «debajo de una foto de cuerpo entero, "Espalda" se lee como una parte del cuerpo, no
// como la postura. "De espaldas" dice lo que quiero decir». Así que se queda su ORDEN
// (frente, perfil, espaldas, que sí cambió) y vuelve el «de» de las tres. Y «Hecha» se
// queda: eso no lo tocó.
// No volver a quitarles el «de» citando su documento: esta respuesta es posterior.
//
// OJO, ESTE COMPONENTE SIRVE TRES PANTALLAS: el paso 3 del reporte mensual, «Tus fotos y
// métricas» de Evolución y la pantalla de fotos del alta. Cambiar aquí cambia las tres, que
// es lo que se quiere: el mismo trío no puede llamarse de dos maneras según por dónde se
// entre. La frase de ayuda de cada una no se toca: esa sí la escribió él y dice cómo
// ponerse, que es otra cosa.
const POSES = [
    { key: 'frente', label: 'De frente', ayuda: 'Brazos relajados a los lados, de pie y recto. Sin meter tripa.' },
    { key: 'perfil', label: 'De perfil', ayuda: 'De lado, brazos relajados, sin girar el cuerpo hacia la cámara.' },
    { key: 'espalda', label: 'De espaldas', ayuda: 'La misma postura, dándole la espalda a la cámara.' },
];

// `onSubida` es opcional: avisa a la pantalla de fuera de que hay una foto nueva, para
// que su propia lista (la rejilla de fotos de progreso) no se quede desfasada.
// `compacta`: la de «Fotos y medidas» del mensual (28-10, maqueta 3): la frase y los tres
// huecos de borde discontinuo, sin cabecera ni las fotos del mes pasado debajo.
// `alCambiarHechas`, opcional: cuántas de las tres lleva subidas hoy (0 a 3), cada vez que
// cambia. Lo usa el check-in diario para encender su «Continuar» con las tres (02-11).
const TresFotos = ({ api, token, esMensual = true, onSubida, compacta = false, alCambiarHechas }) => {
    const [fotos, setFotos] = useState([]);
    // La del mes pasado que se esta mirando en grande, o null.
    const [ampliada, setAmpliada] = useState(null);
    const [subiendo, setSubiendo] = useState(null);
    const [urls, setUrls] = useState({});

    const cargar = useCallback(() => {
        api.get('/reports/photos').then(r => setFotos(r.data?.photos || [])).catch(() => {});
    }, [api]);
    useEffect(() => { cargar(); }, [cargar]);

    // La última de cada pose: es la referencia contra la que se coloca hoy.
    const ultimaDe = (pose) => (fotos || [])
        .filter(f => f.pose === pose)
        .sort((a, b) => String(b.taken_at).localeCompare(String(a.taken_at)))[0];

    // Las fotos van con la sesión, así que se piden con el token y se pintan desde el blob.
    useEffect(() => {
        let vivo = true;
        const base = process.env.REACT_APP_BACKEND_URL || '';
        POSES.forEach(({ key }) => {
            const f = ultimaDe(key);
            if (!f || urls[f.id]) return;
            // ENCOGIDA, QUE ESTO ES UNA FOTO EN PANTALLA Y NO UN ARCHIVO (12-09). Aquí
            // se bajaba el original de cámara -- 1,46 MB de media en producción, 9,6 MB la
            // mayor -- para rellenar un hueco de unos 300 px, y eran otros siete megas de
            // los treinta y dos segundos que tardaba Seguimiento en un teléfono.
            fetch(`${base}/api/reports/photos/${f.id}?w=1080`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => (r.ok ? r.blob() : Promise.reject()))
                .then(b => { if (vivo) setUrls(u => ({ ...u, [f.id]: URL.createObjectURL(b) })); })
                .catch(() => {});
        });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fotos, token]);

    const subir = async (pose, e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setSubiendo(pose);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post(`/reports/photos?pose=${pose}`, fd);
            toast.success('Foto subida');
            cargar();
            onSubida?.();
        } catch (err) {
            toast.error(mensajeDeError(err, 'No hemos podido subirla'));
        } finally {
            setSubiendo(null);
        }
    };

    // El dia LOCAL del cliente, no el UTC (bloque F, 23-08). Y EL DE LA FOTO, TAMBIEN (14-10,
    // REP-02): `taken_at` es un instante en UTC y cortarle los diez primeros caracteres daba
    // el dia de UTC. De 00:00 a 02:00 de España la foto recien subida no era «de hoy»: la
    // casilla no decia «Hecha» y la miniatura salia como «Mes pasado».
    const hoy = hoyLocal();
    const subidaHoy = (pose) => {
        const f = ultimaDe(pose);
        return Boolean(f) && diaLocal(f.taken_at) === hoy;
    };
    const hechasHoy = POSES.filter(({ key }) => subidaHoy(key)).length;
    useEffect(() => { alCambiarHechas?.(hechasHoy); }, [hechasHoy]); // eslint-disable-line react-hooks/exhaustive-deps

    if (compacta) {
        return (
            <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="tres-fotos">
                <p className="text-sm text-muted-foreground leading-snug">
                    Mismas condiciones que el mes pasado: misma luz, misma hora y mismo momento del día.
                </p>
                <div className="grid grid-cols-3 gap-2">
                    {POSES.map(({ key, label }) => {
                        const ya = subidaHoy(key);
                        // LA SUBIDA, A LA VISTA Y A TAMAÑO MEDIO (28-10): el hueco pasa a ser la
                        // foto. Tocándola se cambia por otra.
                        const vista = ya ? urls[ultimaDe(key)?.id] : null;
                        if (vista) {
                            return (
                                <label key={key} className="block cursor-pointer" title="Cambiar la foto">
                                    <span className="relative block aspect-[3/4] rounded-xl overflow-hidden border border-emerald-500/60">
                                        <img src={vista} alt={`${label}, hoy`} className="w-full h-full object-cover"
                                            data-testid={`foto-subida-${key}`} />
                                        {subiendo === key && (
                                            <span className="absolute inset-0 grid place-items-center bg-black/40">
                                                <Loader2 className="w-5 h-5 animate-spin text-white" />
                                            </span>
                                        )}
                                    </span>
                                    <span className="mt-1 flex items-center justify-center gap-1 text-[12px] font-semibold text-emerald-500">
                                        <Check className="w-3.5 h-3.5" /> {label}
                                    </span>
                                    <input type="file" accept="image/*" className="hidden"
                                        data-testid={`foto-${key}`}
                                        onChange={(e) => subir(key, e)} disabled={subiendo !== null} />
                                </label>
                            );
                        }
                        return (
                            <label key={key} title={POSES.find(p => p.key === key).ayuda}
                                className={`h-20 flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed cursor-pointer text-center transition-colors ${
                                    ya ? 'border-emerald-500/60 bg-emerald-500/5' : 'border-foreground/25 hover:border-[#FF671F]/60'}`}>
                                {subiendo === key ? <Loader2 className="w-4 h-4 animate-spin text-brand" />
                                    : ya ? <Check className="w-4 h-4 text-emerald-500" /> : null}
                                <span className={`text-[13px] ${ya ? 'text-emerald-500 font-semibold' : 'text-muted-foreground'}`}>
                                    {ya ? label : `${label} ↑`}
                                </span>
                                <input type="file" accept="image/*" className="hidden"
                                    data-testid={`foto-${key}`}
                                    onChange={(e) => subir(key, e)} disabled={subiendo !== null} />
                            </label>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-2xl p-4" data-testid="tres-fotos">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
                    <Camera className="w-4 h-4 text-foreground/40" />
                </div>
                <div>
                    <p className="text-sm font-bold text-foreground">Tus tres fotos</p>
                </div>
            </div>

            {esMensual && <p className="text-xs text-foreground/50 mb-4 leading-relaxed">
                Colócate como en la foto del mes pasado: misma distancia, misma luz y mismo
                sitio.
            </p>}

            <div className="grid grid-cols-3 gap-3">
                {POSES.map(({ key, label, ayuda }) => {
                    const anterior = ultimaDe(key);
                    const url = anterior && urls[anterior.id];
                    const ya = subidaHoy(key);
                    return (
                        <div key={key}>
                            <label className={`block cursor-pointer rounded-xl border-2 border-dashed transition-colors ${ya ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border hover:border-brand/50'}`}>
                                <div className="aspect-[3/4] flex flex-col items-center justify-center gap-1.5 p-2 text-center">
                                    {subiendo === key ? <Loader2 className="w-5 h-5 animate-spin text-brand" />
                                        : ya ? <Check className="w-5 h-5 text-emerald-500" />
                                        : <Camera className="w-5 h-5 text-foreground/30" />}
                                    <span className={`text-[11px] font-bold ${ya ? 'text-emerald-500' : 'text-foreground/60'}`}>{label}</span>
                                    {ya && <span className="text-[9px] text-foreground/40">Hecha</span>}
                                </div>
                                <input type="file" accept="image/*" className="hidden"
                                    data-testid={`foto-${key}`}
                                    onChange={(e) => subir(key, e)} disabled={subiendo !== null} />
                            </label>

                            <p className="text-[10px] text-foreground/40 mt-1.5 leading-snug">{ayuda}</p>

                            {/* La del mes pasado, para colocarse igual. Se toca y se ve en
                                grande (Francisco, 26-08): a un tercio del ancho del teléfono
                                no se distingue la postura, que es justo para lo que está. */}
                            {/* Y si la última es la que acaba de subir hoy, se dice que es
                                la de hoy (12-10): salía rotulada "Mes pasado" nada más
                                subirla. */}
                            {url && (
                                <div className="mt-2">
                                    <p className="text-[9px] text-foreground/30 uppercase tracking-wider mb-1">{ya ? 'La de hoy' : 'Mes pasado'}</p>
                                    <button type="button" onClick={() => setAmpliada({ url, label, cuando: ya ? 'hoy' : 'mes pasado' })}
                                        aria-label={`Ver en grande tu foto ${label.toLowerCase()} ${ya ? 'de hoy' : 'del mes pasado'}`}
                                        data-testid={`foto-mes-pasado-${key}`}
                                        className="w-full block active:opacity-100 transition-opacity">
                                        <img src={url} alt={`${label}, ${ya ? 'hoy' : 'mes pasado'}`}
                                            className="w-full rounded-lg opacity-60" />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {ampliada && (
                <VisorDeFoto url={ampliada.url} alt={`${ampliada.label}, ${ampliada.cuando || 'mes pasado'}`}
                    pie={`${ampliada.label} · ${ampliada.cuando || 'mes pasado'}`} alCerrar={() => setAmpliada(null)} />
            )}
        </div>
    );
};

export default TresFotos;
